<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * LaisRedactedField — a single masked PII field for the LAIS design system.
 *
 * Shows a label plus either a masked placeholder (when `redacted` and not
 * `revealed`) or the real value. While masked, the real value is NEVER placed
 * in the DOM and the placeholder carries `select-none` so it cannot be copied
 * by accident. Revealing is parent-driven: the component only emits `reveal`
 * (so the owner can audit + flip the `revealed` prop); it never unmasks itself.
 */
const props = defineProps({
  label: { type: String, default: '' },
  value: { type: String, default: '' },
  redacted: { type: Boolean, default: true },
  mask: { type: String, default: '••••••••' },
  reason: { type: String, default: '' },
  canReveal: { type: Boolean, default: false },
  revealed: { type: Boolean, default: false },
  hint: { type: String, default: '' },
});

const emit = defineEmits(['reveal', 'copy']);

const { t } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-redacted-field-${instance?.uid || 'default'}`;
const labelId = `${baseId}-label`;
const valueId = `${baseId}-value`;

const EMPTY_PLACEHOLDER = '—';

const isMasked = computed(() => props.redacted && !props.revealed);
const hasValue = computed(
  () =>
    props.value !== null &&
    props.value !== undefined &&
    String(props.value).length > 0
);

const protectedText = computed(
  () => props.reason || t('LAIS_REDACTED_FIELD.PROTECTED')
);
const maskedAriaLabel = computed(() =>
  props.label ? `${props.label}: ${protectedText.value}` : protectedText.value
);

const showRevealToggle = computed(() => props.redacted && props.canReveal);
const showRequestAccess = computed(
  () => props.redacted && !props.canReveal && isMasked.value
);
const showCopy = computed(() => !isMasked.value && hasValue.value);

const revealToggleLabel = computed(() =>
  isMasked.value
    ? t('LAIS_REDACTED_FIELD.REVEAL')
    : t('LAIS_REDACTED_FIELD.HIDE')
);
const revealToggleIcon = computed(() =>
  isMasked.value ? 'i-lucide-eye' : 'i-lucide-eye-off'
);
const requestAccessLabel = computed(() =>
  t('LAIS_REDACTED_FIELD.REQUEST_ACCESS')
);
// Reuse the shared copy-button label instead of a bespoke key.
const copyLabel = computed(() => t('LAIS_COPY_BUTTON.LABEL'));

const handleRevealToggle = () => emit('reveal', isMasked.value);
const handleRequestAccess = () => emit('reveal', true);
const handleCopy = () => {
  if (!showCopy.value) return;
  emit('copy', props.value);
};
</script>

<template>
  <div
    role="group"
    :aria-labelledby="labelId"
    class="flex min-w-0 flex-col gap-1"
  >
    <span
      :id="labelId"
      class="font-redHatText text-lais-label-sm font-medium text-lais-text-helper"
    >
      {{ label }}
    </span>

    <div class="flex min-w-0 flex-wrap items-center gap-2">
      <span
        v-if="isMasked"
        :id="valueId"
        data-masked="true"
        :aria-label="maskedAriaLabel"
        class="inline-flex select-none items-center font-redHatText text-lais-body-md tracking-[0.18em] text-lais-text-muted"
      >
        <span aria-hidden="true">{{ mask }}</span>
      </span>
      <span
        v-else-if="hasValue"
        :id="valueId"
        data-masked="false"
        :aria-labelledby="labelId"
        class="min-w-0 break-all font-redHatText text-lais-body-md text-lais-text-body"
      >
        {{ value }}
      </span>
      <span
        v-else
        :id="valueId"
        data-masked="false"
        :aria-labelledby="labelId"
        class="font-redHatText text-lais-body-md text-lais-text-muted"
      >
        {{ EMPTY_PLACEHOLDER }}
      </span>

      <button
        v-if="showRevealToggle"
        type="button"
        data-action="reveal"
        class="inline-flex h-7 shrink-0 items-center gap-1 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-2 font-redHatText text-lais-label-xs font-semibold text-lais-text-body transition hover:border-lais-primary/50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-label="revealToggleLabel"
        :aria-pressed="!isMasked"
        @click="handleRevealToggle"
      >
        <span
          class="size-3.5 shrink-0"
          :class="revealToggleIcon"
          aria-hidden="true"
        />
        <span>{{ revealToggleLabel }}</span>
      </button>

      <button
        v-if="showRequestAccess"
        type="button"
        data-action="request"
        class="inline-flex h-7 shrink-0 items-center gap-1 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-2 font-redHatText text-lais-label-xs font-semibold text-lais-text-body transition hover:border-lais-primary/50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-label="requestAccessLabel"
        @click="handleRequestAccess"
      >
        <span class="i-lucide-key-round size-3.5 shrink-0" aria-hidden="true" />
        <span>{{ requestAccessLabel }}</span>
      </button>

      <button
        v-if="showCopy"
        type="button"
        data-action="copy"
        class="grid size-7 shrink-0 place-items-center rounded-lais-sm text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-label="copyLabel"
        :title="copyLabel"
        @click="handleCopy"
      >
        <span class="i-lucide-copy size-3.5" aria-hidden="true" />
      </button>
    </div>

    <p
      v-if="isMasked"
      class="flex items-center gap-1 font-redHatText text-lais-body-xs text-lais-text-helper"
    >
      <span class="i-lucide-lock size-3 shrink-0" aria-hidden="true" />
      <span class="min-w-0 truncate">{{ protectedText }}</span>
    </p>

    <p
      v-if="hint"
      class="font-redHatText text-lais-body-xs text-lais-text-muted"
    >
      {{ hint }}
    </p>
  </div>
</template>
